/**
 * An error indicating that get or set of metadata failed because it has not yet been
 * loaded.
 */
export class MetadataNotLoadedError extends Error {
	public readonly tag = "metadata-not-loaded-error" as const;
	constructor() {
		super("Get or set of metadata failed because it has not yet been loaded");
		this.name = "MetadataNotLoadedError";
	}
}

/**
 * An error indicating that we failed to load the metadata from the collab server.
 */
export class MetadataLoadError extends Error {
	public readonly tag = "metadata-load-error" as const;
	constructor(error: unknown) {
		const msg =
			typeof error === "string"
				? error
				: error instanceof Error
					? error.message
					: "unknown reason";
		super(`Failed to load metadata: ${msg}`);
		this.name = "MetadataLoadError";
	}
}

/**
 * An error indicating metadata file content is in a merge-conflicted state.
 */
export class MetadataConflictedError extends Error {
	public readonly tag = "metadata-conflicted-error" as const;
	constructor(error: unknown) {
		const msg =
			typeof error === "string"
				? error
				: error instanceof Error
					? error.message
					: "unknown reason";
		super(`Document metadata is in a conflicted state: ${msg}`);
		this.name = "MetadataConflictedError";
	}
}

/**
 * An error indicating that we failed to persist the metadata.
 */
export class MetadataPersistError extends Error {
	public readonly tag = "metadata-persist-error" as const;
	constructor(error: unknown) {
		const msg = error instanceof Error ? error.message : "unknown reason";
		super(`Failed to persist metadata: ${msg}`);
		this.name = "MetadataPersistError";
	}
}

/**
 * An error indicating that our subscription to the collab server for metadata has terminated
 * unexpectedly.
 */
export class MetadataCollabSubscriptionError extends Error {
	public readonly tag = "metadata-collab-subscription-error" as const;
	constructor(error: unknown) {
		const msg =
			error instanceof Error ? error.message : "Subscription failed for unknown reason";
		super(`Metadata collab subscription failed unexpectedly: ${msg}`);
		this.name = "MetadataCollabSubscriptionError";
	}
}

/**
 * An error indicating that metadata updates are halted due to a previous critical error.
 */
export class MetadataTransactionProcHaltedError extends Error {
	public readonly tag = "metadata-update-halted-error" as const;
	constructor(error: unknown) {
		const msg = error instanceof Error ? error.message : "unknown reason";
		super(`Metadata updates halted: ${msg}`);
		this.name = "MetadataTransactionProcHaltedError";
	}
}

/** Regex matching jj/git conflict markers in file content. */
export const CONFLICT_MARKER_REGEX = /<<<<<<<|>>>>>>>|\|\|\|\|\|\|\|/;

/**
 * An error indicating that metadata is misconfigured.
 *
 * The main use for this is to indicate that metadata is not configured to write to either collab
 * or disk, which means updates would be lost.
 */
export class MetadataMisconfiguredError extends Error {
	public readonly tag = "metadata-misconfigured-error" as const;
	constructor(message: string) {
		super(message);
		this.name = "MetadataMisconfiguredError";
	}
}
